import { useMemo } from 'react';

import { useSuspenseQuery } from '@/hooks/use-suspense-query';

import { suppliersApi } from './supplier-api';

/**
 * Hook to get a supplier
 */
export function useSupplier(supplierId: string) {
  const { data, ...rest } = useSuspenseQuery(
    suppliersApi.getSupplier(supplierId),
  );

  const transformedData = useMemo(() => {
    return {
      ...data,
      ingredients: data?.ingredient[0].count,
      isManaged: data?.organisation_id == null,
    };
  }, [data]);

  return {
    data: transformedData,
    ...rest,
  };
}
/**
 * Hook to get all Prepsheets' managed suppliers
 */
export function useManagedSuppliers() {
  const { data, ...rest } = useSuspenseQuery(
    suppliersApi.getManagedSuppliers(),
  );

  // TODO: For prepsheets managed suppliers we don't need to get the ingredient count
  const transformedData = useMemo(() => {
    return (data ?? []).map((item) => ({
      ...item,
      isManaged: true,
      ingredients: item.ingredient[0].count,
    }));
  }, [data]);

  return {
    data: transformedData,
    ...rest,
  };
}

/**
 * Hook to get all unmanaged for an organisation.
 */
export function useOrganisationSuppliers(tenantId: string) {
  const { data, ...rest } = useSuspenseQuery(
    suppliersApi.getOrganisationSuppliers(tenantId),
  );

  const transformedData = useMemo(() => {
    return (data ?? []).map(({ ingredient, ...item }) => ({
      ...item,
      isManaged: false,
      ingredients: ingredient[0].count,
    }));
  }, [data]);

  return {
    data: transformedData,
    ...rest,
  };
}

/**
 * Hook to get all managed suppliers for an organisation.
 * TODO: Create a view that encapsulates both managed and unmanaged suppliers for an organisation and gets the ingredient count from that view
 * @returns
 */
export function useOrganisationManagedSuppliers(tenantId: string) {
  const { data, ...rest } = useSuspenseQuery(
    suppliersApi.getOrganisationManagedSuppliers(tenantId),
  );

  const transformedData = useMemo(() => {
    return (data ?? []).map(({ supplier: { ingredient, ...item } }) => ({
      ...item,
      isManaged: true,
      ingredients: ingredient[0].count,
      organisation_id: item.organisation_id,
    }));
  }, [data]);

  return {
    data: transformedData,
    ...rest,
  };
}

/**
 * Hook to get all suppliers for an organisation (both managed and unmanaged).
 * @param id
 * @returns
 */
export function useSuppliers(tenantId: string) {
  const managedQuery = useOrganisationManagedSuppliers(tenantId);
  const orgQuery = useOrganisationSuppliers(tenantId);

  const suppliers = useMemo(
    () => [...managedQuery.data, ...orgQuery.data],
    [managedQuery.data, orgQuery.data],
  );

  return {
    data: suppliers,
    isLoading: managedQuery.isLoading || orgQuery.isLoading,
  };
}

/**
 * Hook to get all managed suppliers for the admin.
 */
export function useAllManagedSuppliers() {
  const { data, ...rest } = useSuspenseQuery(
    suppliersApi.getAllManagedSuppliers(),
  );

  const transformedData = useMemo(() => {
    return (data ?? []).map(({ ingredient, ...item }) => ({
      ...item,
      isManaged: true,
      ingredients: ingredient[0].count,
    }));
  }, [data]);

  return {
    data: transformedData,
    ...rest,
  };
}
